// S2-0f.2: Run Evaluation — 3-state component (Configure → Running → Results)
import { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../utils/api';
import { useJobStream } from '../hooks/useJobStream';
import { useEvidence } from '../hooks/useEvidence';
import type { ConfigPreset, TestProduct, AgentDetail } from '../types/eval';
import type { FlowDefinition } from '../types/flow';
import type { GateEvidence } from '../hooks/useEvidence';
import './RunEvaluation.css';

interface Props {
  agent: AgentDetail;
  onBack: () => void;
}

type ViewState = 'configure' | 'running' | 'results';

// Config descriptions for the dropdown
const CONFIG_DESCS: Record<string, string> = {
  full: 'All layers active: ROLE.md + SKILL.md + tools + schema + quality gate. Production configuration.',
  bare: 'No skill layers. Raw Claude intelligence only. Baseline for ACS comparison.',
  'no-tools': 'Skill + schema active, but tools disabled. Tests how much tool execution matters.',
  'no-schema': 'Skill + tools active, but no domain knowledge. Tests schema contribution.',
  'no-quality-gate': 'Everything active except check_quality. Tests quality gate impact.',
};

export function RunEvaluation({ agent, onBack }: Props) {
  const [view, setView] = useState<ViewState>('configure');

  // Configure state
  const [configs, setConfigs] = useState<ConfigPreset[]>([]);
  const [products, setProducts] = useState<(TestProduct & { selected: boolean })[]>([]);
  const [selectedConfig, setSelectedConfig] = useState('full');
  const [flow, setFlow] = useState<FlowDefinition | null>(null);
  const [loadingPrereqs, setLoadingPrereqs] = useState(true);

  // Running state
  const { status: jobStatus, progress, result, error: jobError, start: startJob, stop: stopJob } = useJobStream();

  // Results state — evidence for deep drill-down
  const [firstRunId, setFirstRunId] = useState<string | null>(null);
  const { evidence } = useEvidence(firstRunId);
  const [expandedGate, setExpandedGate] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Load configs, test set, and flow on mount
  useEffect(() => {
    Promise.all([
      apiFetch<ConfigPreset[]>('/v1/eval/configs'),
      apiFetch<{ products: TestProduct[] }>(`/v1/eval/test-set/${agent.id}`),
      apiFetch<{ flow_definition: FlowDefinition }>(`/v1/eval/agents/${agent.id}/flow`),
    ]).then(([configsData, testSetData, flowData]) => {
      setConfigs(configsData);
      setProducts(testSetData.products.map(p => ({ ...p, selected: true })));
      setFlow(flowData.flow_definition);
      setLoadingPrereqs(false);
    }).catch(() => {
      setLoadingPrereqs(false);
    });
  }, [agent.id]);

  // Timer for running state
  useEffect(() => {
    if (view === 'running' && jobStatus === 'streaming') {
      timerRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [view, jobStatus]);

  // Transition to results when job completes
  useEffect(() => {
    if (jobStatus === 'complete' && result) {
      if (timerRef.current) clearInterval(timerRef.current);
      // Get first run ID for evidence fetch
      const runs = result.results as Record<string, unknown>[] | undefined;
      if (runs && runs.length > 0 && runs[0].run_id) {
        setFirstRunId(runs[0].run_id as string);
      }
      setView('results');
    }
  }, [jobStatus, result]);

  // Helpers
  const selectedProducts = products.filter(p => p.selected);
  const toggleProduct = (id: string) => {
    setProducts(prev => prev.map(p => p.id === id ? { ...p, selected: !p.selected } : p));
  };
  const toggleAll = () => {
    const allSelected = products.every(p => p.selected);
    setProducts(prev => prev.map(p => ({ ...p, selected: !allSelected })));
  };
  const formatTime = (s: number) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  // Start evaluation
  const handleStart = () => {
    setView('running');
    setElapsed(0);
    startJob('/v1/eval/run', {
      agent_id: agent.id,
      config_id: selectedConfig,
      products: selectedProducts.map(p => p.id),
      runs_per_product: 1,
    });
  };

  // Reset to configure
  const handleReset = () => {
    stopJob();
    if (timerRef.current) clearInterval(timerRef.current);
    setView('configure');
    setElapsed(0);
  };

  return (
    <div className="run-eval">
      {/* Header */}
      <div className="run-header">
        <button className="run-back" onClick={onBack} title="Back to overview">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 3L5 7L9 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
        <span className="run-title">Run Evaluation</span>
        <span className="run-agent">for <strong>{agent.name}</strong></span>
      </div>

      {/* ════ STATE 1: CONFIGURE ════ */}
      {view === 'configure' && (
        <>
          {/* Prerequisites */}
          <div className="run-section-label">
            Agent Prerequisites
            <span className="run-section-hint">all must be ready</span>
          </div>
          <div className="prereq-list">
            {[
              { name: 'ROLE.md', detail: 'loaded', pass: true },
              { name: 'SKILL.md', detail: 'product-listing-creator', pass: true },
              { name: `Schema layers (${agent.schema_layers?.length || 0})`, detail: 'merged', pass: (agent.schema_layers?.length || 0) > 0 },
              { name: `Tools (${agent.expected_workflow?.length || 0})`, detail: agent.expected_workflow?.join(', ') || '', pass: (agent.expected_workflow?.length || 0) > 0 },
              { name: 'Test set', detail: `${products.length} products`, pass: products.length > 0 },
            ].map(p => (
              <div key={p.name} className="prereq-item">
                <div className={`prereq-dot ${p.pass ? 'pass' : 'fail'}`}>
                  {p.pass ? (
                    <svg width="10" height="10" viewBox="0 0 16 16" fill="none"><path d="M4 8L7 11L12 5" stroke="var(--success-dark-text)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  ) : (
                    <svg width="10" height="10" viewBox="0 0 16 16" fill="none"><path d="M5 5L11 11M11 5L5 11" stroke="var(--error-dark-text)" strokeWidth="2" strokeLinecap="round"/></svg>
                  )}
                </div>
                <span className="prereq-name">{p.name}</span>
                <span className="prereq-detail">{p.detail}</span>
              </div>
            ))}
          </div>

          {/* Config selector */}
          <div className="run-section-label">Configuration Preset</div>
          <select
            className="config-select"
            value={selectedConfig}
            onChange={e => setSelectedConfig(e.target.value)}
          >
            {configs.map(c => (
              <option key={c.id} value={c.id}>{c.name} — {c.description}</option>
            ))}
            {configs.length === 0 && <option value="full">Full Stack (loading...)</option>}
          </select>
          <div className="config-desc">
            {CONFIG_DESCS[selectedConfig] || 'Select a configuration preset.'}
          </div>

          {/* Product selector */}
          <div className="run-section-label">Test Products</div>
          <div className="product-list">
            <div className="product-list-header">
              <label className="product-select-all" onClick={toggleAll}>
                <div className={`checkbox ${products.every(p => p.selected) ? 'checked' : ''}`} />
                Select All
              </label>
              <span className="product-count">{selectedProducts.length} / {products.length}</span>
            </div>
            {products.map(p => (
              <div key={p.id} className="product-item" onClick={() => toggleProduct(p.id)}>
                <div className={`checkbox ${p.selected ? 'checked' : ''}`} />
                <div>
                  <div className="product-name">{(p.product_data as Record<string, unknown>).product_name as string || p.id}</div>
                  <div className="product-purpose">{p.test_purpose}</div>
                </div>
              </div>
            ))}
            {products.length === 0 && (
              <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                {loadingPrereqs ? 'Loading test products...' : 'No test products available'}
              </div>
            )}
          </div>

          {/* Estimate */}
          <div className="estimate-bar">
            <strong>{selectedProducts.length} products</strong> × {selectedConfig} config × 1 run
            <span className="estimate-time">~{selectedProducts.length * 20}s</span>
          </div>

          {/* Start */}
          <button className="btn-start" onClick={handleStart} disabled={selectedProducts.length === 0 || loadingPrereqs}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M5 3L12 8L5 13V3Z" fill="currentColor"/></svg>
            Start Evaluation
          </button>
        </>
      )}

      {/* ════ STATE 2: RUNNING ════ */}
      {view === 'running' && (
        <>
          {/* Status bar */}
          <div className="run-status-bar">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span className="run-config-badge">{selectedConfig}</span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {selectedProducts.length} products
              </span>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className="run-timer">{formatTime(elapsed)}</div>
              <div className="run-timer-label">Elapsed</div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="run-progress-bar">
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 12, color: 'var(--text-muted)' }}>
              <span>{progress?.product_name ? `Running: ${progress.product_name}` : 'Starting...'}</span>
              <span style={{ fontFamily: 'var(--font-mono)' }}>
                {progress?.current_product || 0} / {progress?.total_products || selectedProducts.length}
              </span>
            </div>
            <div className="run-progress-track">
              <div className="run-progress-fill" style={{ width: `${progress?.percent || 0}%` }} />
            </div>
          </div>

          {/* Product cards — show completed + current + queued */}
          <div className="run-products">
            {selectedProducts.map((product, idx) => {
              const productNum = idx + 1;
              const currentProduct = progress?.current_product || 0;
              const isDone = productNum < currentProduct;
              const isActive = productNum === currentProduct;
              const isQueued = productNum > currentProduct;

              // Find result for this product from progressHistory
              const productResult = (result?.results as Record<string, unknown>[] | undefined)?.find(
                r => r.product_id === product.id
              );

              return (
                <div
                  key={product.id}
                  className={`run-product-card ${isDone ? (productResult ? 'done' : 'done') : isActive ? 'active' : 'queued'}`}
                >
                  <div className="run-product-header">
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span className="run-product-num">{productNum}/{selectedProducts.length}</span>
                      <span className="run-product-name">
                        {(product.product_data as Record<string, unknown>).product_name as string || product.id}
                      </span>
                      {isActive && (
                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--primary-400)', animation: 'pulse 1.5s infinite', marginLeft: 8 }} />
                      )}
                    </div>
                    {isDone && productResult && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="run-product-score pass">
                          {(productResult.quality_overall as number)?.toFixed(2) || '—'}
                        </span>
                        <span className={`run-product-badge ${productResult.classification === 'happy_path' ? 'badge-happy' : 'badge-retry'}`}>
                          {productResult.classification as string || 'done'}
                        </span>
                      </div>
                    )}
                    {isQueued && (
                      <span style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', padding: '2px 8px', background: 'var(--surface-2)', borderRadius: 'var(--radius-sm)' }}>queued</span>
                    )}
                  </div>

                  {/* Flow steps for done/active products */}
                  {(isDone || isActive) && flow && (
                    <div className="flow-steps">
                      {flow.phases.map(phase => (
                        phase.steps.map(step => {
                          const isToolStep = step.type === 'tool_call';
                          const label = isToolStep ? step.name : phase.name;
                          // Simplified status: for done products all steps are done, for active show based on checkpoint
                          const stepState = isDone ? 'done' : (
                            phase.order < 3 ? 'done' :
                            phase.order === 3 && step.order && step.order <= 3 ? 'done' :
                            phase.order === 3 && step.order === 4 ? 'active' :
                            'pending'
                          );

                          // Only show key steps, not all 20
                          if (!isToolStep && phase.id !== 'setup' && phase.id !== 'scoring' && phase.id !== 'storage') return null;
                          if (phase.id === 'setup' && step.id !== 'preflight') return null;
                          if (phase.id === 'scoring' && step.id !== 'quality_scoring') return null;
                          if (phase.id === 'storage' && step.id !== 'save_to_db') return null;

                          return (
                            <div key={`${phase.id}-${step.id}`} className={`flow-step ${stepState}`}>
                              {stepState === 'done' && (
                                <svg width="8" height="8" viewBox="0 0 16 16" fill="none"><path d="M4 8L7 11L12 5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                              )}
                              {stepState === 'active' && (
                                <div style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor', animation: 'pulse 1s infinite' }} />
                              )}
                              {label}
                            </div>
                          );
                        })
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Error state */}
          {jobError && (
            <div style={{ padding: 16, background: 'var(--error-dark-bg)', borderRadius: 'var(--radius-xl)', marginBottom: 'var(--space-5)', color: 'var(--error-dark-text)', fontSize: 13 }}>
              Error: {jobError}
            </div>
          )}

          <button className="btn-cancel" onClick={handleReset}>Cancel Evaluation</button>
        </>
      )}

      {/* ════ STATE 3: RESULTS (P0 Enhanced) ════ */}
      {view === 'results' && result && (
        <>
          {/* ── 1. TOP BAR: Quality + 4 blocking gates ── */}
          <div className={`results-hero ${(result.quality_avg as number) >= 0.85 ? 'pass' : 'fail'}`}>
            <div className={`results-score ${(result.quality_avg as number) >= 0.85 ? 'pass' : 'fail'}`}>
              {(result.quality_avg as number)?.toFixed(3) || '—'}
            </div>
            <div className="results-label">Overall Quality Score</div>

            {/* 4 blocking gate scores */}
            {evidence && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 24, marginTop: 16 }}>
                {evidence.gates.filter(g => g.blocking).map(g => (
                  <div key={g.gate_id} style={{ textAlign: 'center' }}>
                    <div style={{
                      fontFamily: 'var(--font-mono)', fontSize: 18, fontWeight: 700,
                      color: g.pass ? 'var(--success-dark-text)' : 'var(--error-dark-text)',
                    }}>
                      {typeof g.score === 'number' ? g.score.toFixed(2) : g.score}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                      {g.gate_name}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* 2. Brief statement with knob references */}
            <div className="results-summary">
              {evidence?.summary
                ? `${evidence.summary.passing}/${evidence.summary.total_gates} gates pass. ${evidence.summary.blocking.length > 0 ? `Blocked by: ${evidence.summary.blocking.map(b => b.replace('_', ' ')).join(', ')}.` : 'All clear.'} ${evidence.summary.highest_roi_fix}`
                : `${(result.total_evaluations as number) || 0} products evaluated with ${result.config_id} config.`
              }
            </div>

            <div className="results-meta">
              <span><span className="results-meta-value">{elapsed}s</span> total</span>
              <span>Config: <span className="results-meta-value">{String(result.config_id)}</span></span>
              {evidence && <span><span className="results-meta-value">{evidence.summary.critical_products.length}</span> critical products</span>}
            </div>
          </div>

          {/* ── 3. READINESS GATES with info icons + expandable drill-down ── */}
          {evidence && evidence.gates.length > 0 && (
            <>
              <div className="run-section-label">Readiness Gates</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 24 }}>
                {evidence.gates.map((gate: GateEvidence) => (
                  <div key={gate.gate_id}>
                    {/* Gate row — clickable */}
                    <div
                      onClick={() => setExpandedGate(expandedGate === gate.gate_id ? null : gate.gate_id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', cursor: 'pointer',
                        background: !gate.pass ? (gate.blocking ? 'var(--error-dark-bg)' : 'var(--warning-dark-bg)') : 'var(--surface-1)',
                        border: `1px solid ${!gate.pass ? (gate.blocking ? 'rgba(239,68,68,0.2)' : 'rgba(245,158,11,0.2)') : 'var(--border-default)'}`,
                        borderRadius: expandedGate === gate.gate_id ? '8px 8px 0 0' : 8,
                        transition: 'background 0.15s',
                      }}
                    >
                      {/* Pass/fail icon */}
                      <div style={{
                        width: 24, height: 24, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: gate.pass ? 'var(--success-dark-bg)' : (gate.blocking ? 'rgba(239,68,68,0.2)' : 'rgba(245,158,11,0.2)'),
                      }}>
                        {gate.pass
                          ? <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M4 8L7 11L12 5" stroke="var(--success-dark-text)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          : <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M5 5L11 11M11 5L5 11" stroke={gate.blocking ? 'var(--error-dark-text)' : 'var(--warning-dark-text)'} strokeWidth="2" strokeLinecap="round"/></svg>
                        }
                      </div>

                      {/* Gate name + info tooltip */}
                      <span style={{
                        fontSize: 13, fontWeight: 600, width: 120,
                        color: gate.pass ? 'var(--text-primary)' : (gate.blocking ? 'var(--error-dark-text)' : 'var(--warning-dark-text)'),
                      }}>
                        {gate.gate_name}
                      </span>

                      {/* Score bar */}
                      <div style={{ flex: 1, height: 4, background: 'var(--surface-2)', borderRadius: 2, overflow: 'hidden' }}>
                        <div style={{
                          height: '100%', borderRadius: 2, transition: 'width 0.4s',
                          width: `${Math.min(100, (gate.score / Math.max(gate.threshold * 1.2, gate.score)) * 100)}%`,
                          background: gate.pass ? 'var(--success)' : (gate.blocking ? 'var(--error)' : 'var(--warning)'),
                        }} />
                      </div>

                      {/* Score + threshold */}
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, width: 50, textAlign: 'right',
                        color: gate.pass ? 'var(--success-dark-text)' : (gate.blocking ? 'var(--error-dark-text)' : 'var(--warning-dark-text)'),
                      }}>
                        {typeof gate.score === 'number' ? (gate.gate_id === 'G6_cost' ? `${gate.score}¢` : gate.score.toFixed(2)) : gate.score}
                      </span>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-muted)', width: 50 }}>
                        {gate.gate_id === 'G6_cost' ? `≤ ${gate.threshold}¢` : `≥ ${gate.threshold}`}
                      </span>

                      {gate.blocking && !gate.pass && (
                        <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--error-dark-text)', background: 'rgba(239,68,68,0.15)', padding: '2px 6px', borderRadius: 3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Blocking
                        </span>
                      )}

                      {/* Expand chevron */}
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0, transform: expandedGate === gate.gate_id ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                        <path d="M4 5.5L7 8.5L10 5.5" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>

                    {/* ── 4. EXPANDED: sub-metrics + reasoning + fixes ── */}
                    {expandedGate === gate.gate_id && (
                      <div style={{
                        padding: '14px 16px 16px 52px',
                        background: !gate.pass ? (gate.blocking ? 'var(--error-dark-bg)' : 'var(--warning-dark-bg)') : 'var(--surface-1)',
                        border: `1px solid ${!gate.pass ? (gate.blocking ? 'rgba(239,68,68,0.2)' : 'rgba(245,158,11,0.2)') : 'var(--border-default)'}`,
                        borderTop: 'none', borderRadius: '0 0 8px 8px',
                      }}>
                        {/* Reasoning */}
                        <div style={{
                          fontSize: 12, color: 'var(--text-secondary)', marginBottom: 14, lineHeight: 1.6,
                          borderLeft: `2px solid ${gate.pass ? 'var(--success)' : 'var(--error)'}`, paddingLeft: 12,
                        }}>
                          {gate.reasoning}
                        </div>

                        {/* Per-product breakdown */}
                        {gate.product_scores.length > 0 && (
                          <>
                            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>
                              Per-Product ({gate.product_scores.length})
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
                              {gate.product_scores.map(ps => (
                                <div key={ps.product_id} style={{ fontSize: 12, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                  <span style={{ color: ps.pass ? 'var(--success-dark-text)' : 'var(--error-dark-text)', fontWeight: 600, minWidth: 40, fontFamily: 'var(--font-mono)' }}>
                                    {typeof ps.score === 'number' ? ps.score.toFixed(2) : ps.score}
                                  </span>
                                  <div>
                                    <div style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{ps.product_name}</div>
                                    <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 2 }}>{ps.reasoning}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </>
                        )}

                        {/* Improvement opportunity */}
                        {gate.improvement && (
                          <div style={{ fontSize: 12, color: 'var(--info-dark-text)', background: 'var(--info-dark-bg)', padding: '8px 12px', borderRadius: 6, marginBottom: 14 }}>
                            {gate.improvement.roi}
                          </div>
                        )}

                        {/* Knob fixes */}
                        {gate.fixes.length > 0 && (
                          <>
                            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 6 }}>
                              Recommended Fix
                            </div>
                            {gate.fixes.map((f, i) => (
                              <div key={i} style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
                                <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--primary-400)', fontWeight: 600 }}>{f.knob_id}</span> — {f.reason}
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── 5. PER-PRODUCT with all 13 metrics ── */}
          <div className="run-section-label">Product Results</div>
          <div className="results-grid">
            {((result.results as Record<string, unknown>[]) || []).map((r) => {
              const quality = r.quality_overall as number;
              const pass = quality >= 0.85;
              // Find dimension evidence for this product
              const productDims = evidence?.dimensions.map(d => {
                const ps = d.product_scores.find(p => p.product_id === r.product_id);
                return ps ? { ...d, productScore: ps } : null;
              }).filter(Boolean) ?? [];

              return (
                <div key={r.run_id as string} className={`result-card ${pass ? 'pass' : 'fail'}`}>
                  <div className="result-card-top">
                    <div className="result-card-name">{r.product_name as string}</div>
                    <div className={`result-card-score ${pass ? 'pass' : 'fail'}`}>{quality?.toFixed(2)}</div>
                  </div>
                  <div className="result-card-meta">
                    {r.duration_ms ? `${((r.duration_ms as number) / 1000).toFixed(1)}s` : '—'}
                    {r.cost_cents ? ` · ${(r.cost_cents as number).toFixed(1)}¢` : ''}
                    {typeof r.classification === 'string' && (
                      <> · <span className={`run-product-badge ${r.classification === 'happy_path' ? 'badge-happy' : 'badge-retry'}`} style={{ display: 'inline' }}>
                        {r.classification}
                      </span></>
                    )}
                  </div>

                  {/* Quality dimensions (A1-A8) */}
                  {productDims.length > 0 && (
                    <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {productDims.map((dim: any) => (
                        <div key={dim.dimension_id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
                          <span style={{ width: 90, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {dim.name}
                          </span>
                          <div style={{ flex: 1, height: 4, background: 'var(--surface-2)', borderRadius: 2, overflow: 'hidden' }}>
                            <div style={{
                              height: '100%', borderRadius: 2,
                              width: `${(dim.productScore.score * 100)}%`,
                              background: dim.productScore.pass ? 'var(--success)' : (dim.productScore.score >= dim.threshold * 0.8 ? 'var(--warning)' : 'var(--error)'),
                            }} />
                          </div>
                          <span style={{
                            fontFamily: 'var(--font-mono)', fontSize: 10, width: 30, textAlign: 'right',
                            color: dim.productScore.pass ? 'var(--success-dark-text)' : 'var(--error-dark-text)',
                          }}>
                            {dim.productScore.score.toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Actions */}
          <div className="results-actions">
            <button className="btn-run-again" onClick={handleReset}>Run Again</button>
            <button className="btn-back-overview" onClick={onBack}>Back to Overview</button>
          </div>
        </>
      )}

      {/* Error fallback for results */}
      {view === 'results' && !result && jobError && (
        <div style={{ textAlign: 'center', padding: 'var(--space-12)' }}>
          <p style={{ color: 'var(--error-dark-text)', fontSize: 14, marginBottom: 'var(--space-4)' }}>
            Evaluation failed: {jobError}
          </p>
          <button className="btn-run-again" onClick={handleReset} style={{ maxWidth: 200, margin: '0 auto' }}>
            Try Again
          </button>
        </div>
      )}
    </div>
  );
}
